import React, { FC, Fragment, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DocumentNode, useMutation } from '@apollo/client';
import { Button, Chip } from '@mui/material';
import { connectWhiteLabelClinicToLabTemplate } from 'apollo/templates/client-directory';
import { connectLabCustomerTemplate } from 'apollo/templates/client-directory/mutation.graphql';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { DirectoryLab } from 'components/supplier-browser/directory-browser-config';
import RequestConnectionDialog from 'components/clinic-requests/request-connection-dialog.component';
import { SupplierConnectionState } from 'models/supplier-request';

type AddCustomerActionProps = {
    lab: DirectoryLab;
    mutation: DocumentNode;
    variables: Record<string, unknown>;
};

/**
 * DD-3698: makes a clinic or lab the lab's customer, after an "are you sure" — the same
 * mutations as Add customer, reusing the organization. The entry then leaves Find customer,
 * which lists only organizations the lab does not work with yet.
 */
const AddCustomerAction: FC<AddCustomerActionProps> = ({ lab, mutation, variables }) => {
    const target = lab.parent ?? lab;
    const { t: translate } = useTranslation(['client-directory', 'common']);
    const { showBoundary } = useDenthubErrorBoundary();
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [addCustomer, { loading: isAdding }] = useMutation(mutation, {
        variables,
        refetchQueries: ['customerDirectory'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setIsConfirmOpen(false);
            setAppToasterContent({
                isOpen: true,
                severity: 'success',
                message: translate('client-directory:CUSTOMER_ADDED'),
            });
        },
        onError: (error) => {
            setIsConfirmOpen(false);
            showBoundary(error);
        },
    });

    return (
        <Fragment>
            <Button
                variant="contained"
                size="small"
                disabled={isAdding}
                onClick={() => setIsConfirmOpen(true)}
                data-test={`customer-browser.add-${lab._id}`}
            >
                {translate('client-directory:ADD_AS_CUSTOMER')}
            </Button>
            <ConfirmDialog
                open={isConfirmOpen}
                handleClose={() => setIsConfirmOpen(false)}
                dialogTitle={translate('client-directory:ADD_AS_CUSTOMER_TITLE', { name: target.name })}
                dialogContent={translate(...confirmText(lab))}
                confirmBtnLabel={translate('client-directory:ADD_AS_CUSTOMER')}
                declineBtnLabel={translate('common:CANCEL')}
                handleConfirm={() => addCustomer()}
            />
        </Fragment>
    );
};

/**
 * The card's and profile's action, or none (so the profile has no empty footer). Labs and
 * white-label clinics can be added; white label needs the lab's WHITE_LABEL setting, as
 * connectWhiteLabelClinicToLab does. Denthub Pro clinics get requests in DD-3702.
 */
/** DD-3702: a request to this clinic (or its chain's parent) is waiting for an answer. */
const RequestSentChip: FC = () => {
    const { t: translate } = useTranslation(['client-directory']);
    return <Chip size="small" color="warning" label={translate('client-directory:SUPPLIER_REQUEST_PENDING')} />;
};

/** DD-3706: an action-based clinic works only through procurements; nothing to add or ask. */
const ProcurementChip: FC<{ lab: DirectoryLab }> = ({ lab }) => {
    const { t: translate } = useTranslation(['client-directory']);
    return (
        <Chip
            size="small"
            variant="outlined"
            label={translate('client-directory:ACTION_BASED_PROCUREMENT')}
            data-test={`customer-browser.procurement-${lab._id}`}
        />
    );
};

/**
 * DD-3702: asks a Denthub Pro clinic to become a customer; a chain clinic's request goes to
 * its parent (the server resolves it), which the dialog names.
 */
const RequestClinicAction: FC<{ lab: DirectoryLab }> = ({ lab }) => {
    const { t: translate } = useTranslation(['client-directory']);
    const [isRequestOpen, setIsRequestOpen] = useState(false);
    return (
        <Fragment>
            <Button
                variant="contained"
                size="small"
                onClick={() => setIsRequestOpen(true)}
                data-test={`customer-browser.request-${lab._id}`}
            >
                {translate('client-directory:REQUEST_SUPPLIER')}
            </Button>
            <RequestConnectionDialog
                target={
                    isRequestOpen
                        ? { organizationId: lab._id, name: lab.parent?.name ?? lab.name, kind: 'CLINIC' }
                        : undefined
                }
                onClose={() => setIsRequestOpen(false)}
            />
        </Fragment>
    );
};

/**
 * What the confirm says. A chain clinic is never added alone: its parent is, together
 * with all its clinics — said plainly before anything happens.
 */
const confirmText = (lab: DirectoryLab): [string, Record<string, unknown>] => {
    if (lab.parent) {
        return [
            'client-directory:ADD_PARENT_CONFIRM',
            { clinic: lab.name, parent: lab.parent.name, count: lab.parent.clinicCount },
        ];
    }
    if (lab.clinicCount) {
        return ['client-directory:ADD_CHAIN_CONFIRM', { name: lab.name, count: lab.clinicCount }];
    }
    return ['client-directory:ADD_AS_CUSTOMER_CONFIRM', { name: lab.name }];
};

/**
 * The card's and profile's action, or none (so the profile has no empty footer). A chain
 * clinic adds its parent, so the parent's type decides: labs and white-label clinics can be
 * added, white label only with the lab's WHITE_LABEL setting (as connectWhiteLabelClinicToLab
 * enforces). DD-3702: a Denthub Pro clinic is asked instead — it approves first. DD-3706: an
 * action-based clinic (or chain) only gets a procurement chip, whatever its type.
 */
export const renderCustomerAction = (lab: DirectoryLab, canAddWhiteLabel: boolean) => {
    const targetId = lab.parent?._id ?? lab._id;
    const targetType = lab.parent?.customerType ?? lab.services[0];
    if (lab.isActionBased) return <ProcurementChip lab={lab} />;
    if (lab.connection === SupplierConnectionState.PENDING) return <RequestSentChip />;
    if (targetType === 'PRO') return <RequestClinicAction lab={lab} />;
    if (targetType === 'LAB') {
        return (
            <AddCustomerAction lab={lab} mutation={connectLabCustomerTemplate} variables={{ customerId: targetId }} />
        );
    }
    if (canAddWhiteLabel && targetType === 'WHITE_LABEL') {
        return (
            <AddCustomerAction
                lab={lab}
                mutation={connectWhiteLabelClinicToLabTemplate}
                variables={{ labId: currentOrgVar()?._id, clinicId: targetId }}
            />
        );
    }
    return undefined;
};
